import { OrderPopupCard } from "./styled";
import React, { MutableRefObject, useRef, useState } from "react";
import ExistingOrderDetails from "./order-details/existing-order-details";
import BarekamText from "../../components/text";
import { Message, MessageSections } from "../../components/message/message";
import { ProductDetails } from "./products/product-details";
import PaymentDetails from "./payment-details/payment-details";
import { DeliveryAddressSelector } from "./delivery-address-selector/devlivery-address-selector";
import { AdditionalServices } from "./additional-services/additional-services";
import { ActionBar } from "../../components/action-bar/action-bar";
import { OrderModel } from "../../shared/models/order/order-item.model";
import { updateExistingOrder } from "../../../redux/slices/orders-slice";
import ordersService, {
  OrderUpdateMeta,
  validateOrder,
  validateProducts,
} from "../../../services/orders.service";
import { changeActionbarStatus } from "../../../redux/slices/actionbar.slice";
import { ActionbarStatus } from "../../../redux/reducers/actionbar-reducers";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { Product } from "../../shared/models/order/product.model";
import { useNavigate, useOutletContext } from "react-router-dom";
import Template from "../../../management-pages/template-generator/Template";
import { useReactToPrint } from "react-to-print";
import { OutletContext } from "../../../router/router";
import { ActionsByState } from "../../../management-pages/actions/actions-by-state";
import { WarningDialog } from "../../components/dialogs/warning-dialog";
import QRCode from "react-qr-code";
import { OrderStatesEnum } from "../enums/order-states.enum";
import {
  GenericActionTypes,
  InWarehouseActionTypes,
  OrderPopupActionTypes,
} from "../../../management-pages/actions/action-types.enum";
import { ActionKeys } from "../../../management-pages/actions/action-keys.enum";
import { BarekamIcon } from "../../components/icon/barekam-icon";
import { OrderIcons } from "../../components/icon/variants";
import { ActionDialog } from "../../../management-pages/actions/dialogs/action-dialog";
import { getProductStores } from "../../../redux/slices/static-data.slice";
import { OrderPaymentStatusNames } from "../../../actions/types";
import { HistoryPopupType } from "../../../management-pages/actions/dialogs/blocks/history";

const Component = ({
  order,
  productValidationErrors,
  isReadonly,
  showToast,
  loadAndSetOrder,
  mainContentContainerRef,
  setOrderValidationErrors,
  setProductValidationErrors,
}: ExistingOrderPopupProps) => {
  const orderStateId = order?.trackRecord[0]?.state.orderStateId;
  const orderInEnteredState = orderStateId === OrderStatesEnum.entered;
  const readyForPickup = orderStateId === OrderStatesEnum.readyForPickup;

  const [updateData, setUpdateData] = useState<{ [key: string]: any }>({});
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [activeAction, setActiveAction] = useState<string>();

  const outletContext = useOutletContext<OutletContext>();
  const componentRef = useRef<HTMLDivElement>(null);
  const { languageId } = useAppSelector((state) => state.messagesReducer);
  const { matchesPhone } = useAppSelector((state) => state.deviceReducer);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onAfterPrint: async () => {
      order.orderId &&
        (await ordersService.updateById(
          { isLabelPrinted: true },
          order.orderId,
          languageId,
        ));
    },
  });

  function onChange(field: string, value: any) {
    dispatch(changeActionbarStatus(ActionbarStatus.EXISTING_ORDER_MODIFIED));

    setUpdateData((prevState) => {
      if (value) {
        switch (field) {
          case "recipient":
            prevState[field] = value;

            break;
          case "products":
            prevState[field] = value;

            break;
          case "default":
            prevState = { ...prevState, ...value };
        }
      } else {
        prevState[field] = null;
      }

      return { ...prevState };
    });
  }

  function saveOrder() {
    const clonedAndModifiedOrder = { ...order, ...updateData };

    const orderValidationErrors = validateOrder(clonedAndModifiedOrder);
    const productValidationErrors = validateProducts(clonedAndModifiedOrder);

    setProductValidationErrors(productValidationErrors);
    setOrderValidationErrors(orderValidationErrors);

    if (
      order &&
      Object.keys(orderValidationErrors).length === 0 &&
      Object.keys(productValidationErrors).length === 0
    ) {
      updateOrder(updateData, order.orderId as number);
    }
  }

  function showHistory() {
    setActiveAction(GenericActionTypes.SEE_STATUS_HISTORY);
  }

  function showBarcode() {
    setQrDialogOpen(true);
  }

  function updateOrder(state: any, orderId: number): void {
    const updateData = {
      ...state,
      orderId: +orderId,
      products: state.products?.map((product: any) => ({ ...product })),
      shippingInfo: order.shippingInfo,
    };

    dispatch(updateExistingOrder({ updateData, languageId })).then(
      (response) => {
        const updateMeta = response.payload as OrderUpdateMeta;

        if (updateMeta) {
          showToast(true, true);
          dispatch(getProductStores({}));
          navigate("../my");
        } else {
          // todo: validation error
          showToast(false, false);
        }
      },
    );
  }

  function onActionClick(actionId: string) {
    switch (actionId) {
      case OrderPopupActionTypes.PRINT_BLANK:
        handlePrint();
        break;
      case InWarehouseActionTypes.ANNOUNCE:
        setActiveAction(InWarehouseActionTypes.ANNOUNCE);
    }
  }

  function onButtonClick() {
    if (outletContext.isAdmin) {
      showHistory();
    } else if (readyForPickup) {
      showBarcode();
    } else {
      saveOrder();
    }
  }

  const ButtonText = () => {
    if (outletContext.isAdmin) {
      return (
        <BarekamText variant={"m"} fontWeight={700}>
          <Message
            messageKey={"see_history"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>
      );
    } else if (readyForPickup) {
      return (
        <BarekamText variant={"m"} fontWeight={700}>
          <Message messageKey={"see_qr"} sectionId={MessageSections.ORDERS} />
        </BarekamText>
      );
    } else {
      return (
        <BarekamText variant={"m"} fontWeight={700}>
          <Message messageKey={"save"} sectionId={MessageSections.ORDERS} />
        </BarekamText>
      );
    }
  };

  function getAvailableActions() {
    if (outletContext.isWarehouseWorker) {
      return ActionsByState["orderPopup"];
    } else {
      const isUnannounced =
        order &&
        orderStateId === OrderStatesEnum.inWarehouse &&
        order.products.some((product) => typeof product.price !== "number");

      if (
        isUnannounced &&
        !outletContext.isAdmin &&
        !outletContext.isWarehouseWorker
      ) {
        return [
          {
            id: InWarehouseActionTypes.ANNOUNCE,
            name: ActionKeys[InWarehouseActionTypes.ANNOUNCE],
            icon: BarekamIcon({ variant: OrderIcons.history }),
          },
        ];
      }
    }

    return undefined;
  }

  function handleActionDialogClosed() {
    setActiveAction(undefined);
    loadAndSetOrder();
  }

  return (
    <>
      {outletContext.isWarehouseWorker ? (
        <div style={{ display: "none" }}>
          <Template
            ref={componentRef}
            variant={OrderPaymentStatusNames.WAREHOUSE}
            order={order}
          />
        </div>
      ) : (
        ""
      )}

      <ActionBar
        buttonText={<ButtonText />}
        buttonFunction={() => onButtonClick()}
        headerText={<>{order.name}</>}
        mainContentContainerRef={mainContentContainerRef}
        actions={getAvailableActions()}
        actionFunction={(actionId) => onActionClick(actionId)}
        sticky={!matchesPhone}
        withBackButton
      />

      <OrderPopupCard>
        <ExistingOrderDetails
          order={order}
          recipient={updateData?.recipient}
          isReadonly={isReadonly}
          recipientChanged={(recipient) => onChange("recipient", recipient)}
        />
      </OrderPopupCard>

      <div style={{ width: "100%", maxWidth: "43rem", padding: "1rem" }}>
        <BarekamText variant={"m"} bold>
          <Message
            messageKey={"order_products"}
            sectionId={MessageSections.ORDERS}
          />
        </BarekamText>
      </div>

      {/*{isReadonly*/}
      {/*    ? ''*/}
      {/*    : <OrderPopupCard>*/}
      {/*        <BarekamButton type={OrderButtons.ADD_PRODUCT} onClick={() => onAddNewProduct()}>*/}
      {/*            <BarekamText variant={"xl"} width={'unset'} bold>*/}
      {/*                +*/}
      {/*            </BarekamText>*/}

      {/*            <BarekamText variant={"l"} bold>*/}
      {/*                <Message messageKey={'add_another_product'}*/}
      {/*                         sectionId={MessageSections.ORDERS}/>*/}
      {/*            </BarekamText>*/}
      {/*        </BarekamButton>*/}
      {/*    </OrderPopupCard>*/}
      {/*}*/}

      <OrderPopupCard>
        <DeliveryAddressSelector
          order={order}
          updateData={updateData}
          onSelect={(updateData) => onChange("default", updateData)}
          isNewOrder={false}
          isReadonly={isReadonly}
        />
      </OrderPopupCard>

      {(updateData.products || order.products).map(
        (product: Product, index: number) => (
          <ProductDetails
            order={order}
            product={product}
            products={order.products}
            allProductValidationErrors={productValidationErrors}
            setAllProductValidationErrors={setProductValidationErrors}
            isNewOrder={false}
            isReadonly={isReadonly}
            index={index}
            key={`${product.productId}-${index}`}
            // focused={index === focusedProduct}
            onExistingOrderProductChange={(products) =>
              onChange("products", products)
            }
            expandedByDefault
          />
        ),
      )}

      <AdditionalServices
        order={order}
        updateData={updateData}
        onSelect={(updateData) => onChange("default", updateData)}
        isNewOrder={false}
        isReadonly={isReadonly}
      />

      {orderInEnteredState || !orderStateId ? (
        ""
      ) : (
        <OrderPopupCard>
          <PaymentDetails order={order} loadAndSetOrder={loadAndSetOrder} />
        </OrderPopupCard>
      )}

      <WarningDialog
        open={qrDialogOpen}
        handleClose={() => {
          setQrDialogOpen(false);
        }}
        titleKey={"qr_dialog"}
        descriptionIcon={
          <QRCode
            style={{ height: "6rem", width: "6rem" }}
            value={order.internalTrackingNumber}
          />
        }
        descriptionKey={"qr_dialog_description"}
      />

      <ActionDialog
        open={!!activeAction}
        closePopup={() => handleActionDialogClosed()}
        action={activeAction}
        predefinedOrders={[order]}
        historyDialogProps={{ category: 'Order', categoryId: Number(order?.orderId ) }}
      />

    </>
  );
};

const ExistingOrderPopup = React.memo(Component);

interface ExistingOrderPopupProps {
  order: OrderModel;
  setOrder: React.Dispatch<React.SetStateAction<OrderModel | undefined>>;

  orderValidationErrors: { [key: string]: string };
  productValidationErrors: { [key: string]: { [key: string]: string } };
  setOrderValidationErrors: (validations: { [key: string]: string }) => void;
  setProductValidationErrors: (validations: {
    [key: string]: { [key: string]: string };
  }) => void;

  isReadonly: boolean;
  showToast: (success: boolean, withChanges: boolean) => void;
  loadAndSetOrder: () => void;
  mainContentContainerRef: MutableRefObject<HTMLElement | null>;
}

export default ExistingOrderPopup;
